// Edit a plan — resume a draft, or revise after submitting (drift is flagged, never blocked).
// Same fields and paste-lock as fast-capture (/plan/new); prefilled with the teacher's own text.
// Routed by LESSON id (/lesson/:id/edit) because the plan upsert keys on the lesson — a lesson
// whose plan save once failed simply starts empty here instead of being a dead card.
// Owner only (the API enforces it); an approved plan is locked — the Lead re-opens it first.
//
// A DRAFT carries its school week and its lesson here (owner rulings A1 and A3, 21 Oct 2026): the same Week box and
// catch-up option as the new-plan form, so an old draft whose saved week and lesson disagree can be put right in one
// tap; and "Delete this draft" removes a draft that was never sent (the Lead can delete any draft).

import { api, ApiError } from "../api";
import { esc } from "../dom";
import { navigate, registerNavigationGuard } from "../router";
import { appendSourceLines, planningWeekHref } from "../planning-source";
import { initAutosave } from "../autosave";
import { t } from "../i18n";
import { STEPS, serializeSteps, parseSteps, type StepKey } from "../plan-steps";
import { gradeFromClass, loadGrade, schoolWeekNode, termNumber, type CurriculumNode, type GradeCurriculum } from "../curriculum";
import type { WeekClock } from "../clock";
import { icon } from "../icons";
import { pageHeader, rangeLabel, toast } from "../ui";
import { workspaceHtml, mountWorkspace } from "../plan-workspace";
import { mountRichText, type RichTextHandle } from "../rich-text";
import { resourcesCardHtml, mountResources } from "../plan-resources-ui";
import { loadLessonRefs, teacherTitle } from "../lesson-refs";
import { wireDraftDelete } from "../lesson-data";
import { termWeekRange } from "../term-weeks";

interface LessonMeta {
  id: string;
  topic: string;
  class_name: string;
  class_level: string | null;
  week_no: number;
  duration_min: number;
  status: string;
  /** The week of the year plan this lesson is for (added to GET /api/lessons/:id on 15 Oct 2026; absent on an
   *  older server — the materials panel then says the plan is not linked to a week). */
  node_id?: string | null;
  class_id?: string;
  term_id?: string;
}
interface RawPlan {
  id: string;
  prior: string | null;
  misconception: string | null;
  check_for_understanding: string | null;
  materials: string | null;
  procedure: string | null;
}
interface LessonResponse {
  lesson: LessonMeta;
  plan: RawPlan | null;
  can_edit: boolean;
  /** The lesson's own term (21 Oct 2026): the Week picker's weeks and the dates under them. Absent on an older server. */
  term?: { id: string; name: string; start_date: string; end_date: string; week_count: number } | null;
  /** Whether the viewer may delete this draft, and — when not — the plain reason (or, when yes, a word for the Lead). */
  can_delete?: boolean;
  delete_note?: string | null;
}

export async function renderPlanEdit(app: HTMLElement, params: Record<string, string>): Promise<void> {
  const lessonId = params.id!;
  let data: LessonResponse;
  try {
    data = await api<LessonResponse>(`/api/lessons/${encodeURIComponent(lessonId)}`);
  } catch (err) {
    // A draft opens for its writer only. A teacher reaches someone else's draft when the Lead moved a
    // class to them after its first teacher started planning on it (as on the plan page).
    if (err instanceof ApiError && err.status === 403) {
      app.innerHTML = `
        <div class="topbar"><nav class="breadcrumb" aria-label="Breadcrumb"><a data-link href="/plans">My plans</a><span class="sep" aria-hidden="true">›</span><span class="current" aria-current="page">Plan</span></nav></div>
        <h1>Another teacher's plan</h1>
        <p class="empty">Only the teacher who wrote this plan can change it, and only they and the Lead can open it.</p>
        <p><a class="btn secondary" data-link href="/plans">Back to My plans</a></p>`;
      return;
    }
    throw err;
  }
  const { lesson, plan } = data;
  const isDraft = lesson.status === "in_progress" || lesson.status === "not_started";

  if (!data.can_edit) {
    // The Lead opens a teacher's draft here (read-only) and may delete it (owner ruling A1: the Lead can delete any draft).
    // A draft the viewer cannot edit is another teacher's, and only the Lead can open it: Team is the Lead's place for other
    // teachers' work, so the page names it and a delete goes back to it (the Lead reaches this page from several places).
    const leadView = isDraft;
    app.innerHTML = `
      <div class="topbar"><nav class="breadcrumb" aria-label="Breadcrumb"><a data-link href="${leadView ? "/team" : "/plans"}">${leadView ? "Team" : "My plans"}</a><span class="sep" aria-hidden="true">›</span><span class="current" aria-current="page">${esc(lesson.class_name)} · Week ${esc(lesson.week_no)}</span></nav></div>
      <h1>${esc(lesson.topic)}</h1>
      <p class="empty">${isDraft ? "This is a draft. Only the teacher who wrote it can change it." : t("plan.locked")}</p>
      ${plan ? `<a class="btn secondary" data-link href="/plan/${esc(plan.id)}">${t("plan.view_plan")}</a>` : ""}
      ${isDraft && data.can_delete ? `<p><button class="btn secondary sm" id="delete-draft" type="button">Delete this draft</button></p><p class="error" id="err" hidden></p>` : ""}`;
    const del = app.querySelector<HTMLButtonElement>("#delete-draft");
    if (del) {
      wireDraftDelete(del, lessonId, {
        ask: `Delete this draft? ${data.delete_note ?? ""} Tap again`.replace(/\s+/g, " "),
        done: () => { toast("Draft deleted"); navigate(leadView ? "/team" : "/plans"); },
        fail: (message) => { const e = app.querySelector<HTMLElement>("#err"); if (e) { e.textContent = message; e.hidden = false; } },
      });
    }
    return;
  }

  const v = (s: string | null | undefined): string => esc(s ?? "");

  // Prefill the procedure: a structured plan fills the five step boxes; a pre-PROMPT-3 plan (or the
  // legacy combined body) opens as ONE labeled box so nothing is split or lost (src/plan-steps.ts).
  const parsed = parseSteps(plan?.procedure);
  const legacyText = "legacy" in parsed ? parsed.legacy : null;
  const stepTexts = "steps" in parsed ? parsed.steps : null;
  const isLegacy = legacyText !== null;
  const procedureFields = isLegacy
    ? `<label for="proc-legacy">${t("plan.procedure")}</label>
    <p class="hint">${t("plan.procedure_hint")}</p>
    <textarea id="proc-legacy" class="grow" style="min-height:160px">${v(legacyText)}</textarea>`
    : `<h3 class="sub-title">${t("plan.procedure")}</h3>
    <p class="hint">${t("plan.procedure_hint")} Use the buttons above each box for points, numbers and bold.</p>
    ${STEPS.map(
      (s) => `<label for="step-${s.key}" class="step-label"><span class="step-n">${s.label.slice(0, 1)}</span>${s.label.slice(2)}</label>
    <p class="hint">${s.hint}</p>
    <textarea id="step-${s.key}" class="step-box grow">${v(stepTexts?.[s.key])}</textarea>
    <div class="step-extras"><span class="step-chips" data-step-chips="${s.key}" hidden></span><button type="button" class="linkbtn small" data-add-activity="${s.key}">${icon("plus", "ico-sm")} Add your own activity to this step</button></div>`,
    ).join("")}`;

  // The school week and the year plan's lessons, for a draft whose frame can still be put right.
  const grade = gradeFromClass({ name: lesson.class_name, level: lesson.class_level });
  const cur: GradeCurriculum | null = isDraft && grade ? await loadGrade(grade) : null;
  const term = data.term ?? null;
  const termN = term ? termNumber(term) : null;
  if (cur) await loadLessonRefs(); // the lesson list's titles name the unit
  const canFrame = !!cur && !!term && !!termN;
  const nodeOf = (id: string | null | undefined): CurriculumNode | null => (id && cur ? cur.nodes.find((n) => n.id === id) ?? null : null);
  const weekOpts = term ? Array.from({ length: term.week_count }, (_, i) => i + 1).map((w) => `<option value="${w}"${w === lesson.week_no ? " selected" : ""}>${t("common.week_n", { n: w })}</option>`).join("") : "";
  const nodeOpts = cur
    ? [1, 2, 3].map((tn) => `<optgroup label="${t("roadmap.term_n", { n: tn })}">${cur.nodes.filter((n) => n.term === tn).map((n) =>
        `<option value="${esc(n.id)}"${n.id === lesson.node_id ? " selected" : ""}>Term ${n.term} · Week ${n.suggested_week} — ${esc(teacherTitle(n))}</option>`).join("")}</optgroup>`).join("")
    : "";

  // The planning workspace (teacher review, 15 Oct 2026, point 6): "Continue plan" used to open a wall of boxes with
  // nothing to look at. The same plan now opens beside this week's lesson materials, exactly as a new plan does.
  const lessonHref = lesson.node_id
    ? planningWeekHref(lesson.class_id ?? "", lesson.term_id ?? null, lesson.week_no, lesson.node_id, lessonId).replace('/this-week?', `/map/${encodeURIComponent(lesson.node_id)}?`) : "";
  const form = `
    <section class="card form-card pw-frame pw-frame-read">
      <h2 class="card-title">The lesson</h2>
      <p class="pw-frame-line"><span class="class-badge">${esc(lesson.class_name)}</span> <span data-frame-week>${t("common.week_n", { n: lesson.week_no })}</span></p>
      <p class="pw-frame-line" data-frame-topic>${esc(lesson.topic)}</p>
      ${canFrame ? `
      <div class="form-grid"><label for="week">${t("plan.week")}<select id="week">${weekOpts}</select></label></div>
      <p class="hint" id="weekdates"></p>
      <p class="hint" id="stepnote" hidden></p>
      <p id="fixrow" hidden><button type="button" class="btn secondary sm" id="fix-week"></button> <button type="button" class="linkbtn small" id="fix-lesson"></button></p>
      <details class="pw-more">
        <summary>Teach a different week's lesson (catch-up)</summary>
        <label for="node">Which week's lesson do you want to teach?</label>
        <p class="hint">The school week stays the same. Changing the lesson keeps your writing.</p>
        <select id="node"><option value=""${lesson.node_id ? "" : " selected"}>${t("plan.map_unlink")}</option>${nodeOpts}</select>
      </details>` : ""}
      ${lessonHref ? `<p class="small"><a class="linkbtn" data-link data-lesson-link href="${esc(lessonHref)}">Open the lesson page ${icon("chevron", "ico-sm")}</a></p>` : ""}
      ${isDraft && !data.can_delete && data.delete_note ? `<p class="hint" data-delete-note>${esc(data.delete_note)}</p>` : ""}
    </section>
    <section class="card form-card" data-pw-source><p>Loading this week's syllabus…</p></section>
    <section class="card form-card">
      <label for="materials">Objectives / Materials</label>
      <p class="hint">Sourced starting points and your own additions. Values or character connections are optional.</p>
      <textarea id="materials" class="grow">${v(plan?.materials)}</textarea>
    </section>
    ${resourcesCardHtml()}
    <section class="card form-card">
      <h2 class="card-title">${t("plan.thinking")}</h2>
      <p class="hint">${t("plan.thinking_hint")}</p>
      <label for="prior">${t("plan.prior")}</label>
      <p class="hint">${t("plan.prior_hint")}</p>
      <textarea id="prior" class="grow">${v(plan?.prior)}</textarea>

      <label for="misconception">${t("plan.misconception")}</label>
      <p class="hint">${t("plan.misconception_hint")}</p>
      <textarea id="misconception" class="grow">${v(plan?.misconception)}</textarea>

      <label for="cfu">${t("plan.cfu")}</label>
      <p class="hint">${t("plan.cfu_hint")}</p>
      <textarea id="cfu" class="grow">${v(plan?.check_for_understanding)}</textarea>
    </section>
    <section class="card form-card">
      <h2 class="card-title">${t("plan.the_plan")}</h2>
      <div id="clockbanner"></div>
      ${procedureFields}
    </section>`;

  app.innerHTML = `
    ${pageHeader({
      crumbs: [{ label: "My plans", href: "/plans" }, { label: `${lesson.class_name} · Week ${lesson.week_no}` }],
      title: "Your plan",
      sub: "Keep your own teaching decisions. The syllabus and materials are here when you need them.",
      actions: `<span class="sync-chip"><span class="dot"></span><span id="autosave" aria-live="polite"></span></span>`,
    })}
    <p><a class="btn secondary" id="back-week" data-link href="${esc(planningWeekHref(lesson.class_id ?? "", lesson.term_id ?? null, lesson.week_no, lesson.node_id, lessonId))}">Back to this week</a></p>
    ${workspaceHtml(form)}
    <div class="action-bar">
      <div id="err" class="error" hidden></div>
      ${isDraft && data.can_delete ? `<button class="btn secondary sm" id="delete-draft" type="button">Delete this draft</button>` : ""}
      <button class="btn secondary" id="save" type="button">${t("common.save")}</button>
      <button class="btn" id="savesubmit" type="button">${lesson.status === "submitted" ? t("plan.save_resubmit") : t("plan.save_submit")}</button>
    </div>`;

  // Formatting in every writing box (teacher review, point 5), with the same paste lock as /plan/new (spec §2, §4.4):
  // the thinking cells + every procedure box stay the teacher's own English; Materials stays pasteable. Mounting an
  // editor never changes the stored text — only real typing does — so opening a submitted plan writes nothing.
  const rich = new Map<string, RichTextHandle>();
  const procIds = isLegacy ? ["proc-legacy"] : STEPS.map((s) => `step-${s.key}`);
  for (const id of ["prior", "misconception", "cfu", ...procIds]) {
    const box = app.querySelector<HTMLTextAreaElement>(`#${id}`);
    if (box) rich.set(id, mountRichText(box, { pasteLocked: true }));
  }
  const matBox = app.querySelector<HTMLTextAreaElement>("#materials");
  if (matBox) rich.set("materials", mountRichText(matBox, { pasteLocked: false, placeholder: "Aims, books and pages, cards, audio… (you can paste a list here)" }));

  const resources = mountResources(app, { lessonId: () => lessonId, ensureLesson: async () => lessonId, canEdit: data.can_edit });
  const ws = mountWorkspace(app, lesson.node_id ?? null, {
    classId: () => lesson.class_id ?? null,
    lessonId: () => lessonId,
    termId: () => lesson.term_id ?? null,
    weekNo: () => lesson.week_no,
    prepareWorksheet: async () => {
      if (resources.hasUnsavedChanges()) throw new Error("Save or cancel the open materials form first.");
      await autosave.settle(); await persist(false);
    },
    onUseSyllabus: (lines) => {
      const box = app.querySelector<HTMLTextAreaElement>("#materials")!;
      const next = appendSourceLines(box.value, lines);
      if (next !== box.value) {
        box.value = next;
        rich.get("materials")?.refresh();
        box.dispatchEvent(new Event("input", { bubbles: true }));
      }
      toast("Sourced starting points are in Objectives / Materials. Your writing is kept.");
    },
    onAddBooks: (lines) => {
      const box = app.querySelector<HTMLTextAreaElement>("#materials");
      if (!box) return;
      const have = box.value.trim();
      const add = lines.filter((l) => !have.includes(l));
      if (add.length) {
        box.value = (have ? have + "\n" : "") + add.map((l) => `- ${l}`).join("\n");
        rich.get("materials")?.refresh();
        box.dispatchEvent(new Event("input", { bubbles: true }));
      }
      toast(add.length ? "Added to Materials — change anything." : "These books are already in Materials.");
    },
    onAddPages: async (b) => { await resources.addBook(b); },
  });

  // The clock banner (P1b Job 3) — same one-line note as /plan/new, above the step boxes.
  // Decorative: no grade parseable from the class name/level, or the lookup fails → nothing shown.
  if (grade) {
    void api<WeekClock | { clock: null }>(`/api/clock?grade=${grade}`)
      .then((clock) => {
        if (!("week_no" in clock) || clock.teaching_days >= 5) return;
        const holder = app.querySelector<HTMLElement>("#clockbanner");
        if (!holder) return;
        const names = clock.lost_days.map((d) => v(d.label)).join(", ");
        holder.innerHTML = `<p class="hint">${clock.teaching_days} teaching day${clock.teaching_days === 1 ? "" : "s"} this week${names ? ` — ${names}` : ""}.</p>`;
      })
      .catch(() => {});
  }

  const val = (id: string): string => (app.querySelector(id) as HTMLTextAreaElement | null)?.value ?? "";
  const err = app.querySelector<HTMLElement>("#err")!;

  // Arriving from the talk-it-through summary card (?path=dialogue): the plan records which
  // road the THINKING took. Metadata only — every cell below is still typed by the teacher.
  const planPath = new URLSearchParams(location.search).get("path") === "dialogue" ? "dialogue" : undefined;
  // Re-serialize the five step boxes into the single procedure column; a legacy plan saves its one
  // box as-is (still legacy format — never silently re-structured).
  const procedureText = (): string =>
    isLegacy
      ? val("#proc-legacy")
      : serializeSteps(Object.fromEntries(STEPS.map((s) => [s.key, val(`#step-${s.key}`)])) as Record<StepKey, string>);
  const planBody = () => ({
    prior: val("#prior"),
    misconception: val("#misconception"),
    check_for_understanding: val("#cfu"),
    materials: val("#materials"),
    procedure: procedureText(),
    ...(planPath ? { path: planPath } : {}),
  });

  // Autosave with a dirty check against what's already stored: merely OPENING a submitted plan
  // never writes (no passive drift) — only real typing saves, on a quiet 2s debounce.
  let savedPlan = JSON.stringify(planBody());
  let planId: string | null = plan?.id ?? null;

  async function persist(keepalive: boolean): Promise<boolean> {
    const pJson = JSON.stringify(planBody());
    if (pJson === savedPlan) return false;
    const saved = await api<{ plan_id: string }>(`/api/lessons/${encodeURIComponent(lessonId)}/plan`, {
      method: "PUT",
      body: pJson,
      keepalive,
    });
    planId = saved.plan_id;
    savedPlan = pJson;
    return true;
  }

  const autosave = initAutosave({ indicator: app.querySelector<HTMLElement>("#autosave")!, save: persist });
  app.querySelectorAll("textarea").forEach((el) => {
    el.addEventListener("input", () => autosave.schedule());
  });
  const unregisterGuard = registerNavigationGuard(app, async () => {
    try {
      if (resources.hasUnsavedChanges()) throw new Error("Save or cancel the open materials form before leaving.");
      await autosave.settle(); await persist(false); autosave.dispose(); return true;
    }
    catch (ex) { err.textContent = `Your plan could not be saved. ${(ex as Error).message} Stay here and retry.`; err.hidden = false; return false; }
  });

  // ── the school week and the lesson (a draft only) ──
  const weekSel = app.querySelector("#week") as HTMLSelectElement | null;
  const nodeSel = app.querySelector("#node") as HTMLSelectElement | null;
  const weekDates = (w: number): string => {
    if (!term) return "";
    const r = termWeekRange({ start_date: term.start_date, end_date: term.end_date }, w);
    return rangeLabel(r.start, r.end);
  };
  /** Redraw everything that names the week or the lesson from the lesson as the server now holds it. */
  function refreshFrame(): void {
    const w = lesson.week_no;
    const node = nodeOf(lesson.node_id);
    const dates = weekDates(w);
    app.querySelector("[data-frame-week]")!.textContent = t("common.week_n", { n: w });
    app.querySelector("[data-frame-topic]")!.textContent = lesson.topic;
    app.querySelector(".crumbs [aria-current='page']")?.replaceChildren(`${lesson.class_name} · Week ${w}`);
    app.querySelector<HTMLAnchorElement>("#back-week")?.setAttribute("href", planningWeekHref(lesson.class_id ?? "", lesson.term_id ?? null, w, lesson.node_id, lessonId));
    const link = app.querySelector<HTMLAnchorElement>("[data-lesson-link]");
    if (link) {
      if (lesson.node_id) link.setAttribute("href", planningWeekHref(lesson.class_id ?? "", lesson.term_id ?? null, w, lesson.node_id, lessonId).replace("/this-week?", `/map/${encodeURIComponent(lesson.node_id)}?`));
      else link.hidden = true;
    }
    if (weekSel) weekSel.value = String(w);
    if (nodeSel) nodeSel.value = lesson.node_id ?? "";
    ws.setNode(lesson.node_id ?? null);
    if (!canFrame) return;
    const datesEl = app.querySelector<HTMLElement>("#weekdates");
    if (datesEl) datesEl.textContent = dates ? `${t("common.week_n", { n: w })} runs ${dates}.` : "";
    const note = app.querySelector<HTMLElement>("#stepnote")!;
    const fixRow = app.querySelector<HTMLElement>("#fixrow")!;
    const fixWeek = app.querySelector<HTMLButtonElement>("#fix-week")!;
    const fixLesson = app.querySelector<HTMLButtonElement>("#fix-lesson")!;
    const sameWeek = !!node && node.term === termN && node.suggested_week === w;
    note.hidden = true;
    fixRow.hidden = true;
    if (node && !sameWeek) {
      const where = node.term === termN ? `Week ${node.suggested_week}` : `Term ${node.term} Week ${node.suggested_week}`;
      note.textContent = `This plan is saved for school week ${w}, but it teaches ${where}'s lesson.`;
      note.hidden = false;
      // One tap puts it right: make the plan the lesson's own week, or teach the school week's own lesson instead.
      // (style, not `hidden`: .btn's and .linkbtn's display outrank the attribute)
      const canMoveWeek = node.term === termN && !!term && node.suggested_week >= 1 && node.suggested_week <= term.week_count;
      fixWeek.style.display = canMoveWeek ? "" : "none";
      fixWeek.textContent = `Make this the Week ${node.suggested_week} plan`;
      const own = termN ? schoolWeekNode(cur!, termN, w) : null;
      const canMoveLesson = !!own?.node && own.node.id !== node.id;
      fixLesson.style.display = canMoveLesson ? "" : "none";
      fixLesson.textContent = `Teach Week ${w}'s lesson instead`;
      fixRow.hidden = !canMoveWeek && !canMoveLesson;
    }
    if (node) {
      const title = teacherTitle(node);
      ws.setHeading(sameWeek
        ? `Week ${w}${dates ? ` · ${dates}` : ""} · ${title}`
        : `School week ${w}${dates ? ` · ${dates}` : ""} · teaching ${node.term === termN ? "" : `Term ${node.term} `}Week ${node.suggested_week}'s lesson · ${title}`);
    } else {
      ws.setHeading(null);
    }
  }
  /** Change the lesson's week or lesson. The server says no, in a plain sentence, when a teaching record, chosen activities,
   *  a paper, an after-lesson report or a saved worksheet holds the session in place — the page shows it and puts the pickers back. */
  async function applyFrame(patch: Record<string, unknown>): Promise<void> {
    err.hidden = true;
    try {
      await autosave.settle();
      await persist(false); // the teacher's writing is saved first, in order
      const res = await api<{ lesson: { week_no: number; node_id: string | null; topic: string } }>(`/api/lessons/${encodeURIComponent(lessonId)}`, { method: "PATCH", body: JSON.stringify(patch) });
      lesson.week_no = res.lesson.week_no;
      lesson.node_id = res.lesson.node_id;
      lesson.topic = res.lesson.topic;
    } catch (ex) {
      err.textContent = (ex as Error).message;
      err.hidden = false;
    }
    refreshFrame(); // from the lesson as the server holds it — after a refusal the pickers go back to what is saved
  }
  /** The topic follows the lesson only while it is still the lesson's own title (the teacher's own words are never replaced). */
  const topicFor = (from: CurriculumNode | null, to: CurriculumNode | null): Record<string, string> =>
    from && to && lesson.topic === teacherTitle(from) ? { topic: teacherTitle(to) } : {};
  if (canFrame) {
    weekSel?.addEventListener("change", () => {
      const w = Number(weekSel.value);
      const hit = schoolWeekNode(cur!, termN!, w);
      void applyFrame({ week_no: w, ...(hit.node ? { node_id: hit.node.id, ...topicFor(nodeOf(lesson.node_id), hit.node) } : {}) });
    });
    nodeSel?.addEventListener("change", () => {
      const next = nodeOf(nodeSel.value);
      void applyFrame({ node_id: nodeSel.value || null, ...topicFor(nodeOf(lesson.node_id), next) });
    });
    app.querySelector("#fix-week")?.addEventListener("click", () => {
      const node = nodeOf(lesson.node_id);
      if (node) void applyFrame({ week_no: node.suggested_week });
    });
    app.querySelector("#fix-lesson")?.addEventListener("click", () => {
      const own = schoolWeekNode(cur!, termN!, lesson.week_no).node;
      if (own) void applyFrame({ node_id: own.id, ...topicFor(nodeOf(lesson.node_id), own) });
    });
    refreshFrame();
  }

  // ── deleting the draft (owner ruling A1): two taps, no browser dialog ──
  const del = app.querySelector<HTMLButtonElement>("#delete-draft");
  if (del) {
    wireDraftDelete(del, lessonId, {
      ask: `Delete this draft? ${data.delete_note ?? ""} Tap again`.replace(/\s+/g, " "),
      before: () => autosave.settle(),
      done: () => { unregisterGuard(); autosave.dispose(); toast("Draft deleted"); navigate("/plans"); },
      fail: (message) => { err.textContent = message; err.hidden = false; },
    });
  }

  async function save(submit: boolean): Promise<void> {
    err.hidden = true;
    try {
      await autosave.settle(); // never race a tick that's already in flight
      await persist(false);
      if (submit) await api(`/api/lessons/${encodeURIComponent(lessonId)}/submit`, { method: "POST" });
      autosave.dispose();
      if (planId) navigate(`/plan/${planId}`);
      else navigate("/this-week"); // nothing was ever saved and nothing typed — just leave calmly
    } catch (ex) {
      err.textContent = (ex as Error).message;
      err.hidden = false;
    }
  }

  app.querySelector<HTMLButtonElement>("#save")!.addEventListener("click", () => void save(false));
  app.querySelector<HTMLButtonElement>("#savesubmit")!.addEventListener("click", () => void save(true));
  if (new URLSearchParams(location.search).get("stage") === "materials") app.addEventListener("eden:mounted", () => {
    app.querySelector<HTMLElement>("#own-materials")?.scrollIntoView({ block: "start" });
    app.querySelector<HTMLElement>('[data-pr-new="file"]')?.focus({ preventScroll: true });
  }, { once: true });
}
